// Tasks on a phone (UX-20261031-05): one card per task on the shared mobile
// operations list instead of the wide table. The whole card opens the task;
// "Done" closes it in one tap (UX-20261031-06) when the person may change tasks.
import { Link } from '@tanstack/react-router'
import { Check, ChevronDown, ChevronRight } from 'lucide-react'
import { MobileOperationsList } from '../../components/operations/mobile-operations-list'
import { Badge } from '../../components/ui/badge'
import { Button } from '../../components/ui/button'
import { cn } from '../../lib/utils'
import { useT } from '../../i18n'
import { staffTasksI18n } from '../../i18n/shared/staff-tasks'
import type { StaffTask, TaskStatus } from './api'
import { DUE_BUCKETS, type DueBucket } from './dates'
import { assigneeNames, formatDay, taskDetailRoute, type TaskShell } from './lib'

const STATUS_BADGE: Record<TaskStatus, 'neutral' | 'warning' | 'success'> = {
  todo: 'neutral',
  in_progress: 'warning',
  done: 'success',
}

type Props = {
  shell: TaskShell
  items: StaffTask[]
  loading: boolean
  error: boolean
  onRetry: () => void
  /** One-tap "Done"; left out when the person may not change tasks. */
  onDone?: (task: StaffTask) => void
  /** Due-date groups (My tasks, one person), same as the table. */
  groups?: Record<DueBucket, StaffTask[]>
  collapsed?: Set<DueBucket>
  onToggleGroup?: (bucket: DueBucket) => void
}

export function TaskMobileList({
  shell,
  items,
  loading,
  error,
  onRetry,
  onDone,
  groups,
  collapsed,
  onToggleGroup,
}: Props) {
  const t = useT(staffTasksI18n)
  const labels = { loading: t.quick.loading, error: t.loadError, retry: t.quick.retry, empty: t.empty.filteredTitle }
  const list = (rows: StaffTask[]) => (
    <MobileOperationsList
      items={rows}
      loading={loading}
      error={error}
      getKey={(task) => task.id}
      labels={labels}
      onRetry={onRetry}
      renderItem={(task) => <TaskCard task={task} shell={shell} onDone={onDone} />}
    />
  )

  return (
    // Bottom room so the last card clears the floating help button.
    <div data-testid="staff-task-cards" className="pb-[calc(env(safe-area-inset-bottom)+4.5rem)]">
      <div className="overflow-hidden rounded-[var(--radius-panel)] border border-[var(--color-border)] bg-[var(--color-surface)]">
      {groups && !loading && !error
        ? DUE_BUCKETS.filter((bucket) => groups[bucket].length > 0).map((bucket) => {
            const open = !collapsed?.has(bucket)
            return (
              <section key={bucket} data-testid={`staff-task-card-group-${bucket}`}>
                <button
                  type="button"
                  aria-expanded={open}
                  aria-label={t.quick.toggleGroup(t.buckets[bucket])}
                  onClick={() => onToggleGroup?.(bucket)}
                  className={cn(
                    'flex min-h-11 w-full items-center gap-1 border-b border-[var(--color-border)] bg-[var(--table-header-bg)] px-4 text-left text-sm font-semibold focus-visible:outline-none focus-visible:shadow-[var(--shadow-focus)]',
                    bucket === 'overdue' ? 'text-[var(--color-danger)]' : 'text-[var(--color-text)]',
                  )}
                >
                  {open ? <ChevronDown className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
                  {t.buckets[bucket]}
                  <span className="font-normal text-[var(--color-text-muted)]">· {groups[bucket].length}</span>
                </button>
                {open && list(groups[bucket])}
              </section>
            )
          })
        : list(items)}
      </div>
    </div>
  )
}

function TaskCard({
  task,
  shell,
  onDone,
}: {
  task: StaffTask
  shell: TaskShell
  onDone?: (task: StaffTask) => void
}) {
  const t = useT(staffTasksI18n)
  const due = formatDay(task.due_date)
  const done = task.status === 'done'
  return (
    <article data-testid={`staff-task-card-${task.id}`} className="flex items-center gap-3 px-4 py-3">
      <Link
        {...taskDetailRoute(shell, task.id)}
        className="flex min-h-11 min-w-0 flex-1 flex-col justify-center gap-1 rounded-md text-left outline-none focus-visible:shadow-[var(--shadow-focus)]"
      >
        <span
          className={cn(
            'line-clamp-2 break-words text-[15px] font-semibold text-[var(--color-text)]',
            done && 'text-[var(--color-text-muted)] line-through',
          )}
        >
          {task.title}
        </span>
        <span className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-[var(--color-text-muted)]">
          <Badge variant={STATUS_BADGE[task.status]}>{t.status[task.status]}</Badge>
          {task.priority === 'urgent' && <Badge variant="destructive">{t.priority.urgent}</Badge>}
          <span className={cn(task.is_overdue && 'font-medium text-[var(--color-danger)]')}>
            {due ?? t.noDueDate}
            {task.is_overdue && <span className="sr-only"> ({t.overdue})</span>}
          </span>
          <span className="min-w-0 truncate">
            {task.assignees.length > 0 ? assigneeNames(task.assignees, t.structure.more) : t.unassigned}
          </span>
          {task.subtasks_total > 0 && (
            <span className="tabular-nums">
              {t.structure.subtasks} {t.structure.subtaskProgress(task.subtasks_done, task.subtasks_total)}
            </span>
          )}
        </span>
      </Link>
      {onDone && !done && (
        <Button
          type="button"
          variant="outline"
          className="shrink-0 gap-1 px-3"
          aria-label={t.quick.markDone(task.title)}
          onClick={() => onDone(task)}
          data-testid={`staff-task-done-${task.id}`}
        >
          <Check className="h-4 w-4" aria-hidden />
          {t.quick.done}
        </Button>
      )}
    </article>
  )
}
